/** Keep homepage copy aligned with `client-app/index.html` meta + Organization JSON-LD. */
const SITE_ORIGIN = 'https://gunslop.com'

export const HOME_PAGE_DESCRIPTION =
  'GunSlop is a community platform for firearm enthusiasts. Searchable Registry, community-verified parts, build documentation, and a reputation system that rewards real knowledge.'

export const ABOUT_PAGE_DESCRIPTION =
  'GunSlop is a community platform for firearm enthusiasts: searchable Registry, Parts Bin, build documentation, and a reputation system that surfaces reliable voices.'

const DEFAULT_TITLE = 'GunSlop'

type PageSeo = { title: string; description: string; canonicalPath: string; ogTitle: string }

function seo (
  title: string,
  ogTitle: string,
  description: string,
  canonicalPath: string
): PageSeo {
  return { title, ogTitle, description, canonicalPath }
}

/** Exact pathname → SEO (checked first). */
const ROUTES: Record<string, PageSeo> = {
  '/': seo(DEFAULT_TITLE, DEFAULT_TITLE, HOME_PAGE_DESCRIPTION, '/'),
  '/about': seo('About GunSlop | GunSlop', 'About GunSlop', ABOUT_PAGE_DESCRIPTION, '/about'),
  '/registry': seo(
    'Firearm Registry | GunSlop',
    'Firearm Registry',
    'Browse the GunSlop Registry—searchable firearm models, community-verified specs, and notes from owners.',
    '/registry'
  ),
  '/registry/new': seo(
    'Add to Registry | GunSlop',
    'Add to Registry',
    'Contribute a firearm model to the GunSlop Registry—specs and documentation verified by the community.',
    '/registry/new'
  ),
  '/registry/full-form': seo(
    'Registry full form | GunSlop',
    'Registry full form',
    'Extended Registry entry on GunSlop—detailed fields for community-verified firearm model data.',
    '/registry/full-form'
  ),
  '/parts-bin': seo(
    'Parts Bin | GunSlop',
    'Parts Bin',
    'Explore the GunSlop Parts Bin—compatibility notes, fit intel, and community-sourced part information.',
    '/parts-bin'
  ),
  '/builds': seo(
    'Builds | GunSlop',
    'Builds',
    'Browse documented builds on GunSlop—part lists, photos, and configurations shared by owners.',
    '/builds'
  ),
  '/builds/new': seo(
    'New build | GunSlop',
    'New build',
    'Document a new firearm build on GunSlop—parts, photos, and configuration for the community.',
    '/builds/new'
  ),
  '/blog': seo(
    'Blog | GunSlop',
    'Blog',
    'GunSlop blog—reviews, ownership tips, discussions, and write-ups from firearm enthusiasts.',
    '/blog'
  ),
  '/ammo': seo(
    'Ammo | GunSlop',
    'Ammo',
    'GunSlop ammo hub—products, cartridge specs, and reloading resources from the community.',
    '/ammo'
  ),
  '/ammo/submit': seo(
    'Submit ammo | GunSlop',
    'Submit ammo',
    'Submit ammo product data to GunSlop—help expand the community cartridge and product catalog.',
    '/ammo/submit'
  ),
  '/ammo/reloading': seo(
    'Reloading | GunSlop',
    'Reloading',
    'GunSlop reloading—handload recipes, load discussion, and practical notes from reloaders.',
    '/ammo/reloading'
  ),
  '/ammo/reloading/new': seo(
    'New load recipe | GunSlop',
    'New load recipe',
    'Share a reloading recipe on GunSlop—components, charge weights, and safety-minded notes.',
    '/ammo/reloading/new'
  ),
  '/slop-feed': seo(
    'Slop Feed | GunSlop',
    'Slop Feed',
    'GunSlop Slop Feed—community photos and posts from shooters. Browse what people are sharing.',
    '/slop-feed'
  ),
  '/gun-vault': seo(
    'Gun Vault | GunSlop',
    'Gun Vault',
    'GunSlop Gun Vault—track firearms, wishlists, and collections with privacy controls you set.',
    '/gun-vault'
  ),
  '/inventory': seo(
    'Inventory | GunSlop',
    'Inventory',
    'Manage your parts inventory on GunSlop—quantities, locations, and links to the Parts Bin.',
    '/inventory'
  ),
  '/discover': seo(
    'Discover members | GunSlop',
    'Discover members',
    'Discover people on GunSlop—profiles, reputation, and activity across the community.',
    '/discover'
  ),
  '/search': seo(
    'Search | GunSlop',
    'Search',
    'Search GunSlop—the Registry, Parts Bin, builds, blog, ammo, and more in one place.',
    '/search'
  ),
  '/login': seo(
    'Sign in | GunSlop',
    'Sign in',
    'Sign in to GunSlop—Registry, Parts Bin, builds, and community features for firearm enthusiasts.',
    '/login'
  ),
  '/register': seo(
    'Create account | GunSlop',
    'Create account',
    'Join GunSlop—contribute to the Registry and Parts Bin, document builds, and earn reputation.',
    '/register'
  ),
  '/forgot-password': seo(
    'Forgot password | GunSlop',
    'Forgot password',
    'Reset your GunSlop password—secure account recovery for Registry and community access.',
    '/forgot-password'
  ),
  '/reset-password': seo(
    'Reset password | GunSlop',
    'Reset password',
    'Choose a new GunSlop password to secure your account and saved content.',
    '/reset-password'
  ),
  '/legacy-following-feed': seo(
    'Following | GunSlop',
    'Following',
    'Your following feed on GunSlop—updates from people and content you follow.',
    '/legacy-following-feed'
  )
}

/**
 * Longest matching prefix wins. Order: more specific paths first.
 * Only used when `pathname` is not an exact key in `ROUTES`.
 */
const PREFIX_RULES: Array<{ prefix: string; seo: PageSeo }> = [
  {
    prefix: '/slop-feed/shoot/',
    seo: seo(
      'Shoot | GunSlop',
      'Shoot',
      'Shoot album on GunSlop Slop Feed—photos and notes from a range day or outing.',
      '' // canonicalPath filled at runtime
    )
  },
  {
    prefix: '/ammo/products/',
    seo: seo(
      'Ammo product | GunSlop',
      'Ammo product',
      'Ammo product details on GunSlop—specifications and community context.',
      ''
    )
  },
  {
    prefix: '/ammo/specs/',
    seo: seo(
      'Cartridge spec | GunSlop',
      'Cartridge spec',
      'Cartridge specification on GunSlop—dimensions, lineage, and related products.',
      ''
    )
  },
  {
    prefix: '/ammo/reloading/',
    seo: seo(
      'Load recipe | GunSlop',
      'Load recipe',
      'Handloading recipe on GunSlop—components, data, and notes from the community.',
      ''
    )
  },
  {
    prefix: '/blog/',
    seo: seo(
      'Blog post | GunSlop',
      'Blog post',
      'GunSlop blog article—reviews, tips, or discussion from the community.',
      ''
    )
  },
  {
    prefix: '/registry/',
    seo: seo(
      'Registry model | GunSlop',
      'Registry model',
      'Firearm model in the GunSlop Registry—specs, community notes, and related builds.',
      ''
    )
  },
  {
    prefix: '/parts/',
    seo: seo(
      'Part | GunSlop',
      'Part',
      'Part details on GunSlop—compatibility, fit notes, and links from the Parts Bin.',
      ''
    )
  },
  {
    prefix: '/builds/',
    seo: seo(
      'Build | GunSlop',
      'Build',
      'Documented build on GunSlop—parts list, photos, and owner configuration.',
      ''
    )
  },
  {
    prefix: '/gun-vault/',
    seo: seo(
      'Firearm | GunSlop',
      'Firearm',
      'Firearm record in your GunSlop Gun Vault—details you chose to save and organize.',
      ''
    )
  },
  {
    prefix: '/inventory/',
    seo: seo(
      'Inventory item | GunSlop',
      'Inventory item',
      'Inventory item on GunSlop—part quantities, notes, and catalog links.',
      ''
    )
  },
  {
    prefix: '/slop-feed/',
    seo: seo(
      'Slop post | GunSlop',
      'Slop post',
      'Slop Feed post on GunSlop—community photo or update from a member.',
      ''
    )
  },
  {
    prefix: '/profiles/',
    seo: seo(
      'Profile | GunSlop',
      'Profile',
      'GunSlop member profile—public activity, reputation, and community presence.',
      ''
    )
  }
]

function setMeta (selector: string, content: string): void {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!el) {
    const isOg = selector.includes('property=')
    el = document.createElement('meta')
    if (isOg) {
      const m = /property="([^"]+)"/.exec(selector)
      if (m) el.setAttribute('property', m[1])
    } else {
      const m = /name="([^"]+)"/.exec(selector)
      if (m) el.setAttribute('name', m[1])
    }
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function normalizePath (pathname: string): string {
  if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.slice(0, -1) || '/'
  }
  return pathname || '/'
}

function resolveSeo (path: string): PageSeo {
  const exact = ROUTES[path]
  if (exact) {
    return exact
  }
  for (const rule of PREFIX_RULES) {
    if (path.startsWith(rule.prefix)) {
      return {
        ...rule.seo,
        canonicalPath: path
      }
    }
  }
  return {
    title: DEFAULT_TITLE,
    ogTitle: DEFAULT_TITLE,
    description: HOME_PAGE_DESCRIPTION,
    canonicalPath: path
  }
}

/**
 * Updates document title and meta tags after navigation. Crawlers that execute JS
 * (including Google) can use these for snippets; static `index.html` remains the
 * pre-hydration fallback for `/`.
 */
export function applyPageSeo (pathname: string): void {
  const path = normalizePath(pathname)
  const seoResolved = resolveSeo(path)

  document.title = seoResolved.title
  setMeta('meta[name="description"]', seoResolved.description)
  setMeta('meta[property="og:title"]', seoResolved.ogTitle)
  setMeta('meta[property="og:description"]', seoResolved.description)
  const canonicalUrl = `${SITE_ORIGIN}${seoResolved.canonicalPath === '/' ? '/' : seoResolved.canonicalPath}`
  setMeta('meta[property="og:url"]', canonicalUrl)

  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', canonicalUrl)
}
